import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { UserPreferences } from '@/types/userPreferences';
import { 
  getUserPreferences, 
  updateUserPreferences, 
  togglePreference as togglePreferenceService,
  resetToDefaultPreferences 
} from '@/services/userPreferences';

/**
 * Context type definition for User Preferences
 * Provides state and operations for managing user preferences throughout the app
 */
interface UserPreferencesContextType {
  // State
  preferences: UserPreferences | null;
  loading: boolean;
  error: Error | null;
  
  // Operations
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => Promise<void>;
  
  togglePreference: (
    key: keyof Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'theme' | 'theme_color' | 'notification_frequency' | 'preferences'>
  ) => Promise<void>;
  
  refresh: () => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * User Preferences Context
 * Must be accessed via useUserPreferences hook
 */
const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

/**
 * User Preferences Provider Component
 * Wraps the app to provide user preferences state and operations to all child components
 * Must be placed inside AuthProvider as it depends on authenticated user
 * 
 * @param children - Child components that will have access to user preferences
 * 
 * @example
 * ```tsx
 * <AuthProvider>
 *   <UserPreferencesProvider>
 *     <App />
 *   </UserPreferencesProvider>
 * </AuthProvider>
 * ```
 */
export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Effect 1 - Initial Load
  // Fetches user preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setLoading(true);
        setError(null);
        const prefs = await getUserPreferences();
        setPreferences(prefs);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Real-time subscription handled by usePreferencesRealtimeUpdates in useUserPreferences hook
  // (single channel via useRealtimeUpdates.ts — duplicate removed to reduce egress)

  /**
   * Update a specific preference field
   * @param key - The preference field to update
   * @param value - The new value for the field
   */
  const updatePreference = useCallback(async <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ): Promise<void> => {
    try {
      setError(null);
      const updated = await updateUserPreferences({ [key]: value });
      if (updated) {
        setPreferences(updated);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to update preference');
      setError(errorObj);
      throw errorObj;
    }
  }, []);

  /**
   * Toggle a boolean preference field
   * @param key - The boolean preference field to toggle
   */
  const togglePreference = useCallback(async (
    key: keyof Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'theme' | 'theme_color' | 'notification_frequency' | 'preferences'>
  ): Promise<void> => {
    try {
      setError(null);
      const updated = await togglePreferenceService(key);
      if (updated) {
        setPreferences(updated);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to toggle preference');
      setError(errorObj);
      throw errorObj;
    }
  }, []);

  /**
   * Refresh preferences from the database
   * Clears errors on success
   */
  const refresh = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const prefs = await getUserPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh preferences'));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset all preferences to default values
   * Clears errors on success
   */
  const reset = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const prefs = await resetToDefaultPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to reset preferences'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      preferences,
      loading,
      error,
      updatePreference,
      togglePreference,
      refresh,
      reset,
    }),
    [preferences, loading, error, updatePreference, togglePreference, refresh, reset]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

/**
 * Hook to access user preferences context
 * Must be used within UserPreferencesProvider
 * 
 * @throws Error if used outside of UserPreferencesProvider
 * @returns User preferences context with state and operations
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { preferences, updatePreference } = useUserPreferences();
 *   
 *   return (
 *     <div>
 *       Dark Mode: {preferences?.dark_mode ? 'ON' : 'OFF'}
 *       <button onClick={() => updatePreference('dark_mode', !preferences?.dark_mode)}>
 *         Toggle
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return context;
};

/**
 * Convenience hook to access and update a specific preference field
 * Returns a tuple of [value, updateFunction] similar to useState
 * 
 * @param key - The preference field to access
 * @returns Tuple of [current value, update function]
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [darkMode, setDarkMode] = usePreference('dark_mode');
 *   
 *   return (
 *     <button onClick={() => setDarkMode(!darkMode)}>
 *       Dark Mode: {darkMode ? 'ON' : 'OFF'}
 *     </button>
 *   );
 * }
 * ```
 */
export const usePreference = <K extends keyof UserPreferences>(
  key: K
): [UserPreferences[K] | undefined, (value: UserPreferences[K]) => Promise<void>] => {
  const { preferences, updatePreference } = useUserPreferences();
  const value = preferences?.[key];
  
  const update = useCallback(async (newValue: UserPreferences[K]) => {
    await updatePreference(key, newValue);
  }, [key, updatePreference]);
  
  return [value, update];
};
